import React from 'react';
import {
  CalendarOutlined,
  CarOutlined,
  CheckCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { Select } from 'antd';
import type { SelectProps } from 'antd';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  getTransportStatusDisplay,
  getTransportStatusDisplayTranslationKey,
} from '../modules/transports/transportRouteViews';
import './TransportStatusPicker.css';

type TransportStatusPickerProps = Omit<SelectProps, 'options'>;

const selectableStatuses = [
  'SCHEDULED',
  'IN_TRANSIT',
  'DELIVERED',
] as const;

function renderStatusOption(value: string, t: TFunction): React.ReactNode {
  const display = getTransportStatusDisplay(value);
  const Icon = display === 'planned'
    ? CalendarOutlined
    : display === 'inProgress'
      ? CarOutlined
      : display === 'completed'
        ? CheckCircleOutlined
        : StopOutlined;

  return (
    <span className="transport-status-picker__option">
      <span className={`transport-status-picker__icon transport-status-picker__icon--${display}`}>
        <Icon />
      </span>
      <span>{t(getTransportStatusDisplayTranslationKey(value))}</span>
    </span>
  );
}

const TransportStatusPicker: React.FC<TransportStatusPickerProps> = ({
  className,
  popupClassName,
  classNames,
  ...selectProps
}) => {
  const { t } = useTranslation(['transport']);

  return (
    <Select
      {...selectProps}
      className={['transport-status-picker', className].filter(Boolean).join(' ')}
      classNames={{
        ...classNames,
        popup: {
          ...classNames?.popup,
          root: [
            'transport-status-picker__popup',
            classNames?.popup?.root,
            popupClassName,
          ].filter(Boolean).join(' '),
        },
      }}
      labelRender={({ value }) => renderStatusOption(String(value), t)}
      options={selectableStatuses.map((value) => ({
        value,
        label: renderStatusOption(value, t),
      }))}
    />
  );
};

export default TransportStatusPicker;
